import React, { useEffect, useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { useRecoilState } from 'recoil';
import { patientAccessionManagerTableParams } from '../../../common/services/PatientService';
import { fetcher } from '../../../common/swr/fetcher';
import { Table, Select, Modal } from 'antd';
import Moment from 'moment';
import { useForm, Controller } from "react-hook-form";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import MaskedInput from 'react-text-mask'
import ReactLoading from 'react-loading';
import { combineLatest } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { toast } from '@rickylandino/react-messages';

import { firstBy } from "thenby";
import { groupBy } from '../../Helpers/GroupBy';

import { userAssociatedFacilities, allCategories } from '../../../common/services/ApiService';
import { updateAccessionNumber } from '../../../common/services/ExamService';

export function ManageAccessionNumbers(props) {

    const { register, control, getValues, setValue, watch } = useForm();
    let userInfo = JSON.parse(window.sessionStorage.getItem("userInfo"));
    const [tableParams, setTableParams] = useRecoilState(patientAccessionManagerTableParams);
    const [filterString, setFilterString] = useState('');
    const key = `/api/GetPatientsToManageAccessionNo?userID=${userInfo.userId}&pageSize=${tableParams.pagination.pageSize}&offset=${tableParams.pagination.current - 1}${filterString ? filterString : ''}`;
    const { data, error, isLoading } = useSWR(key, fetcher);
    const { mutate } = useSWRConfig();

    const [facilityList, setFacilityList] = useState([]);
    const [categoryList, setCategoryList] = useState([]);
    const [examTypeOptions, setExamTypeOptions] = useState({});

    const [displayData, setDisplayData] = useState([]);

    const [modal, setModal] = useState({
        show: false,
        selectedExamId: null
    });

    useEffect(() => {
        combineLatest(
            userAssociatedFacilities,
            allCategories
        ).pipe(takeWhile(() => true)).subscribe(([af, ac]) => {
            if (af?.length > 0 && facilityList.length === 0) {
                setFacilityList(af);
            }

            if (ac?.length > 0 && categoryList.length === 0) {
                setCategoryList(ac);
            }
        });
    }, [facilityList, categoryList]);

    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (name === 'facilityID' && type === 'change') {
                if (value.facilityID) {
                    modifyExamTypes(value.facilityID);
                }

            }
        });

        return () => subscription.unsubscribe();
    }, [watch, categoryList, facilityList]);

    useEffect(() => {
        if (data?.totalCount >= 0) {
            setTableParams({
                ...tableParams,
                pagination: {
                    ...tableParams.pagination,
                    total: data.totalCount
                }
            });

            setDisplayData(data.data);
        }
    }, [data]);

    if (isLoading) return <ReactLoading className='tableLoading' type={"spokes"} color={'#F30079'} height={30} width={30} />;

    const patientColumns = [
        {
            dataIndex: 'lName',
            title: 'Last Name',
        }, {
            dataIndex: 'fName',
            title: 'First Name'
        }, {
            dataIndex: 'mrn',
            title: 'MRN'
        }, {
            dataIndex: 'facilityName',
            title: 'Facility'
        }, {
            dataIndex: 'examDateTime',
            title: 'Exam Date/Time',
            render: cell => Moment(new Date(cell)).format('L')
        }, {
            dataIndex: 'examType',
            title: 'Exam Type'
        }, {
            dataIndex: 'accessionNumber',
            title: 'Accession No'
        },
    ];

    const handleTableChange = (pagination, filters, sorter) => {
        setTableParams({
            pagination,
            filters,
            ...sorter,
        });
    };

    function modifyExamTypes(facility) {
        let availableExamTypes = facilityList.find(f => f.facilityModel.facilityID === facility)?.availableExamTypes || [];
        
        let fet = [...availableExamTypes];
        fet.sort(firstBy("categoryID"));

        var o = groupBy(fet, 'categoryID');

        var sortedExamTypes = [];

        for (const property in o) {
            o[property].sort(
                firstBy("examType")
            );

            sortedExamTypes.push(...o[property]);
        }

        let eto = [];

        sortedExamTypes.forEach((type, idx) => {

            if (type.categoryID !== sortedExamTypes[idx - 1]?.categoryID) {
                let newLabelObj = { label: categoryList?.find(cat => cat.categoryID === type.categoryID)?.description, options: [] };
                eto.push(newLabelObj);
            }

            let newOption = { label: type.examType, value: type.examTypeID };
            eto[eto.length - 1].options.push(newOption);
        });

        setExamTypeOptions(eto);
    }

    function handleSearch() {

        setTableParams({
            ...tableParams,
            pagination: {
                ...tableParams.pagination,
                current: 1
            }
        });

        let filterFields = { ...getValues() };
        let ff = '';

        for (const property in filterFields) {
            if (filterFields[property]) {
                if ((Array.isArray(filterFields[property]) && filterFields[property].length > 0)) {
                    ff += `&filters=${property}:in:${filterFields[property]}`;
                } else if (!Array.isArray(filterFields[property])) {
                    if (property === 'examDateTime') {
                        ff += `&filters=${property}:eq:${filterFields[property].toLocaleDateString()}`;
                    } else {
                        ff += `&filters=${property}:cn:${filterFields[property]}`;
                    }
                }
            }
        }

        //if (state.selectedFacilities?.length > 0) {
        //    ff += `&filters=facilityID:in:'${state.selectedFacilities.join("','")}'`;
        //}

        //if (state.selectedIssuers?.length > 0) {
        //    ff += `&filters=issuerofPatientID:in:'${state.selectedIssuers.join("','")}'`;
        //}

        //if (state.selectedInsurance?.length > 0) {
        //    ff += `&filters=insuranceID:in:'${state.selectedInsurance.join("','")}'`;
        //}

        setFilterString(ff);
    }

    function closeModal() {
        setModal({
            show: false,
            selectedExamId: null,
            selectedIdx: null
        });
    }

    function handleSubmit() {
        let postdata = {
            patientExamID: modal.selectedExamId,
            accessionNumber: getValues().newAccessionNumber
        }

        updateAccessionNumber(postdata).then((data) => {
            if (data) {
                mutate(key);
                closeModal();
                toast.success("Accession number updated successfully");
            } else {
                toast.error("Something went wrong");
            }
        })
    }

    let { Option } = Select;

    return (
        <>
            <div className="row">
                <div className="form-group col-lg-4 col-12">
                    <label className="form-label">Last Name</label>
                    <input type="text" name="lName" {...register('lName')} className="form-control-custom" />
                </div>
                <div className="form-group col-lg-4 col-12">
                    <label className="form-label">First Name</label>
                    <input type="text" name="fName" {...register('fName')} className="form-control-custom" />
                </div>
                <div className="form-group col-lg-4 col-12">
                    <label className="form-label">MRN</label>
                    <input type="text" name="mrn" {...register('mrn')} className="form-control-custom" />
                </div>
                <div className="form-group col-lg-4 col-12">
                    <label className="form-label">Exam Date</label>
                    <Controller
                        control={control}
                        name="examDateTime"
                        render={({ field: { onChange, onBlur, value, ref } }) =>
                            <DatePicker
                                onChange={onChange}
                                className="form-control-custom"
                                selected={value}
                                customInput={
                                    <MaskedInput
                                        mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                                        keepCharPositions={true}
                                        guide={true}
                                    />

                                }
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                            />
                        }
                    />

                </div>
                <div className="form-group col-lg-4 col-12">
                    <label className="form-label">Facility</label>
                    <Controller
                        control={control}
                        name="facilityID"
                        render={({ field: { onChange, onBlur, value, ref } }) =>
                            <Select
                                allowClear
                                placeholder="Please select"
                                onChange={onChange}
                                className="form-control-custom w-100"
                                bordered={false}
                                value={value}
                                showSearch
                                filterOption={(input, option) => (option.children[0] + option.children[1]).toLowerCase().includes(input.toLowerCase())}
                            >
                                {facilityList.map((fac, idx) => <Option key={idx} key={fac.facilityModel.facilityID} value={fac.facilityModel.facilityID}>{fac.facilityModel.facilityNumber && fac.facilityModel.facilityNumber + " - "}{fac.facilityModel.facilityName}</Option>)}
                            </Select>
                        }
                    />
                </div>
                <div className="form-group col col-lg-4 col-12">
                    <div className="form-label">Exam Types</div>
                    {examTypeOptions.length > 0 ?
                        <Controller
                            control={control}
                            name="examTypes"
                            render={({ field: { onChange, onBlur, value, ref } }) =>
                                <Select
                                    mode="multiple"
                                    maxTagCount={1}
                                    allowClear
                                    placeholder="Please select"
                                    onChange={onChange}
                                    className="form-control-custom w-100"
                                    bordered={false}
                                    virtual={false}
                                    value={value || []}
                                    options={examTypeOptions}
                                />
                            }
                        />
                        :
                        <div>Please select a facility first</div>
                    }
                </div>
                <div className="form-group col-lg-4 col-12">
                    <label className="form-label">Accession Number</label>
                    <input type="text" name="mrn" {...register('accessionNumber')} className="form-control-custom" />
                </div>

                <div className="form-group col-lg-2 col-12 mt-4">
                    <button className="btn btn-submit" type="submit" onClick={handleSearch}>Search</button>
                </div>

            </div>

            <h2>Manage Accession Numbers</h2>
            {displayData?.length > 0 &&
                <Table
                    rowKey={(record) => record.patientExamID}
                    dataSource={displayData}
                    columns={patientColumns}
                    size='small'
                    onChange={handleTableChange}
                    pagination={tableParams.pagination}
                    rowClassName={(record, rowIndex) => {
                        if (modal.selectedIdx === rowIndex) {
                            return 'ant-table-row-selected';
                        }
                    }}
                    onRow={(record, rowIndex) => {
                        return {
                            onClick: event => {
                                setValue('newAccessionNumber', record.accessionNumber);

                                setModal({
                                    show: true,
                                    selectedExamId: record.patientExamID,
                                    selectedIdx: rowIndex
                                });
                            }, // click row
                            onDoubleClick: event => { }, // double click row
                            onContextMenu: event => { }, // right button click row
                            onMouseEnter: event => { }, // mouse enter row
                            onMouseLeave: event => { }, // mouse leave row
                        };
                    }}
                />
            }

            <Modal
                title={`Modify Accession Number`}
                visible={modal.show}
                onCancel={closeModal}
                onOk={handleSubmit}
            >
                <div className="row">
                    <div className="form-group col-12">
                        <label className="form-label">Accession Number</label>
                        <input className="form-control-custom" {...register('newAccessionNumber')} maxLength="16" />
                    </div>
                </div>
            </Modal>
        </>
    );
}